import { groupSymbolDataUrl, recolorGroupSymbolSvgCached } from "./groupSymbolSvg";

/**
 * The vehicle symbols of the map, as images.
 *
 * Shared by the map page and the checklist's location map, so a vehicle looks the same on both.
 */

/** Size of the arrow that sits on the ring for a vehicle whose own symbol does not turn. */
export const HEADING_MARKER_SIZE_PX = 14;


export function svgToDataURL(svgMarkup: string) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
}

const NAVIGATION_PATH = "M12 2 4.5 20.29l.71.71L12 18l6.79 3 .71-.71z";
const SQUARE_ROUNDED_PATH =
  "M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2";

/** An icon as deck.gl wants it. Kept whole so the same object is handed back every frame. */
export interface MapIcon {
  url: string;
  width: number;
  height: number;
}

/**
 * How many built icons each cache holds on to.
 *
 * A colour input reports a new colour on every movement of the pointer, so one drag through the
 * picker asks for dozens of colours that will never be asked for again. Kept for the lifetime of
 * the page they add up -- a recoloured group symbol can be forty kilobytes of data URL. The
 * oldest is dropped once the cache is full, which for a fleet drawn in a handful of colours
 * never happens.
 */
const MAX_CACHED_ICONS = 64;

/** Drops the oldest entries once a cache has grown past {@link MAX_CACHED_ICONS}. */
export function capCache(cache: globalThis.Map<string, unknown>) {
  for (const key of cache.keys()) {
    if (cache.size <= MAX_CACHED_ICONS) break;
    cache.delete(key);
  }
}

/**
 * The vehicle icons in one colour.
 *
 * Cached per colour because a fleet coloured by group asks for the same handful of colours over
 * and over, and each miss builds two data URLs.
 */
const vehicleIconCache = new globalThis.Map<string, { driving: MapIcon; stopped: MapIcon }>();
export function getVehicleIcons(color: string) {
  const cached = vehicleIconCache.get(color);
  if (cached) return cached;

  const { driving, stopped } = buildIconMapping(color);
  const icons = {
    driving: { url: driving, width: 128, height: 128 },
    stopped: { url: stopped, width: 128, height: 128 },
  };
  vehicleIconCache.set(color, icons);
  capCache(vehicleIconCache);
  return icons;
}

/**
 * An uploaded group symbol as an icon, cached so the same markup is not turned into a URL again
 * on every frame.
 */
const groupSymbolCache = new globalThis.Map<string, MapIcon>();
export function groupSymbolIcon(svg: string, color: string): MapIcon {
  const cacheKey = `${color}::${svg}`;
  const cached = groupSymbolCache.get(cacheKey);
  if (cached) return cached;

  const icon = {
    url: groupSymbolDataUrl(recolorGroupSymbolSvgCached(svg, color)),
    width: 128,
    height: 128,
  };
  groupSymbolCache.set(cacheKey, icon);
  capCache(groupSymbolCache);
  return icon;
}

/** A small triangle, drawn pointing up so a rotation can aim it anywhere. */
const HEADING_MARKER_PATH = "M12 3 4 21h16z";
const headingMarkerCache = new globalThis.Map<string, MapIcon>();

/**
 * The arrow that shows which way a vehicle is going when its symbol does not turn.
 *
 * A group's own symbol is drawn as its author drew it, upright — a lorry lying on its side is not
 * a heading. So the heading moves out to the edge of the ring as a separate mark.
 */
export function getHeadingMarker(color: string): MapIcon {
  const cached = headingMarkerCache.get(color);
  if (cached) return cached;

  const icon = {
    url: svgToDataURL(
      `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${HEADING_MARKER_PATH}" fill="${color}" stroke="#fff" stroke-width="1.5"/></svg>`,
    ),
    width: 128,
    height: 128,
  };
  headingMarkerCache.set(color, icon);
  capCache(headingMarkerCache);
  return icon;
}

export function buildIconMapping(color: string) {
  const driving = svgToDataURL(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${NAVIGATION_PATH}" fill="${color}"/></svg>`,
  );
  const stopped = svgToDataURL(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${SQUARE_ROUNDED_PATH}" fill="${color}"/></svg>`,
  );
  return { driving, stopped };
}
